import {
  Create,
  CrudProvider,
  DataField,
  DataModel,
  DataSource,
  DataSourceCache,
  Edit,
  List,
  OmitId,
  Show,
} from "@toolpad/core/Crud";
import {
  GridFilterInputValue,
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { axiosInstance } from "../core/axios-interceptors";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { identityService } from "../services/identity.service";
import { getBaseUrl } from "../core/config";
import { CRUDLocaleText } from "@toolpad/core/Crud/localeText";
import { Box, Select } from "@mui/material";
import { useI18n } from "../i18n-context";
import { formatDateWithLocale } from "../utils/date.utils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

export type MagicCrudFields<TModel> = DataField & {
  extraType?: "guid";
  notInCreateMode?: boolean;
  notInEditMode?: boolean;
  notInGridMode?: boolean;
  notInShowMode?: boolean;
  getValueToShow?: (data: TModel) => Promise<string | undefined>;
  isRequired?: boolean;
};

interface MagicCrudProps<TModel extends DataModel> {
  name: string;
  appUrl: string;
  apiUrl: string;
  columns: MagicCrudFields<TModel>[];
  pageSize?: number;
  disable?: {
    create?: boolean;
    edit?: boolean;
    show?: boolean;
    delete?: boolean;
  };
  customCreateOne?: (data: Partial<OmitId<TModel>>) => TModel | Promise<TModel>;
  customCreateUrl?: string;
  customCreateLabel?: string;
  trasformDataForServer?: (data: TModel) => any;
  /** Use a custom field as row id (e.g. when API returns shopSubscriptionId instead of id). */
  getRowId?: (row: TModel) => string | number;
}

function extractId(path: string, pattern: string): string | null {
  const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, "([^/]+)")}$`);
  const match = path.match(regex);
  return match ? match[1] : null;
}

function lowercaseFirstLetterKeys<T extends Record<string, any>>(
  array: T[]
): Record<string, any>[] {
  return array.map((obj) => {
    const newObj: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
        newObj[newKey] = obj[key];
      }
    }
    return newObj;
  });
}

export default function MagicCrud<TModel extends DataModel>({
  name,
  appUrl,
  apiUrl,
  columns,
  pageSize = 20,
  disable,
  customCreateOne,
  customCreateUrl,
  customCreateLabel,
  trasformDataForServer,
  getRowId,
}: MagicCrudProps<TModel>) {
  const intl = useIntl();
  const { locale } = useI18n();
  const intlName = intl.formatMessage({ id: name });
  const crudLocaleText: CRUDLocaleText = {
    createNewButtonLabel: intl.formatMessage({
      id: customCreateLabel ?? "createNewButtonLabel",
    }),
    reloadButtonLabel: intl.formatMessage({ id: "reloadButtonLabel" }),
    createLabel: intl.formatMessage({ id: customCreateLabel ?? "createLabel" }),
    createSuccessMessage: intl.formatMessage({ id: "createSuccessMessage" }),
    createErrorMessage: intl.formatMessage({ id: "createErrorMessage" }),
    editLabel: intl.formatMessage({ id: "editLabel" }),
    editSuccessMessage: intl.formatMessage({ id: "editSuccessMessage" }),
    editErrorMessage: intl.formatMessage({ id: "editErrorMessage" }),
    deleteLabel: intl.formatMessage({ id: "deleteLabel" }),
    deleteConfirmTitle: intl.formatMessage({ id: "deleteConfirmTitle" }),
    deleteConfirmMessage: intl.formatMessage({ id: "deleteConfirmMessage" }),
    deleteConfirmLabel: intl.formatMessage({ id: "deleteConfirmLabel" }),
    deleteCancelLabel: intl.formatMessage({ id: "deleteCancelLabel" }),
    deleteSuccessMessage: intl.formatMessage({ id: "deleteSuccessMessage" }),
    deleteErrorMessage: intl.formatMessage({ id: "deleteErrorMessage" }),
    deletedItemMessage: intl.formatMessage({ id: "deletedItemMessage" }),
  };

  const editLocaleText = {
    ...crudLocaleText,
    editLabel: intl.formatMessage({ id: "save" }),
  };

  const escapeValue = (val: string) => val.replace(/'/g, "''");
  const fullUrl = `${getBaseUrl()}/${apiUrl}`;

  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const listPath = appUrl;
  const createPath = `${appUrl}/new`;
  const showPath = `${appUrl}/:id`;
  const editPath = `${appUrl}/:id/edit`;

  const idFromShow = extractId(pathname, showPath);
  const idFromEdit = extractId(pathname, editPath);
  const isListMode = pathname === listPath;
  const isCreateMode = pathname === createPath;
  const isEditMode = !!idFromEdit;
  const isShowMode = pathname !== createPath && idFromShow && !idFromEdit;

  const mapColums = (): DataField[] => {
    const result: DataField[] = columns
      .filter(
        (c: MagicCrudFields<TModel>) =>
          !(
            (c.notInGridMode && isListMode) ||
            (c.notInShowMode && isShowMode) ||
            (c.notInEditMode && isEditMode) ||
            (c.notInCreateMode && isCreateMode)
          )
      )
      .map((c: MagicCrudFields<TModel>) => {
        const label = intl.formatMessage({ id: c.headerName ?? c.field });
        const requiredSuffix =
          (isCreateMode || isEditMode) && c.isRequired ? " *" : "";
        const isDate = c.type === "date" || c.type === "dateTime";
        const kind = c.type === "dateTime" ? "dateTime" : "date";
        return {
          ...c,
          field: c.field,
          headerName: label + requiredSuffix,
          width: c.width ?? 180,
          align: "left",
          headerAlign: "left",
          ...(isDate && {
            valueFormatter: (value: Date | string | null | undefined) =>
              formatDateWithLocale(value ?? undefined, locale, kind),
          }),
          filterOperators:
            c.extraType === "guid" && c.type !== "singleSelect"
              ? [
                  {
                    value: "equals",
                    getApplyFilterFn: (filterItem) => {
                      if (!filterItem.value) return null;
                      return (params) => params.value === filterItem.value;
                    },
                    InputComponent: GridFilterInputValue,
                  },
                ]
              : undefined,
        };
      });
    return result;
  };

  const internalColums = useMemo(
    () => mapColums(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, intl, locale, isCreateMode, isEditMode, isListMode, isShowMode]
  );

  const buildODataFilter = (filters: GridFilterItem[]) => {
    return filters
      .map((f) => {
        const field = f.field;
        const col = columns.find((c) => c.field === field);
        const colType = col?.type || "string";
        const rawValue = f.value;

        if (!["isNotEmpty", "isEmpty"].includes(f.operator) && !rawValue) {
          return "";
        }

        const value =
          (colType === "string" && col?.extraType !== "guid") ||
          (colType === "singleSelect" && col?.extraType !== "guid")
            ? `'${escapeValue(rawValue.toString())}'`
            : colType === "date"
              ? `${new Date(rawValue).toISOString()}`
              : rawValue;

        switch (f.operator) {
          case "=":
          case "equals":
          case "is":
            return `${field} eq ${value}`;
          case "!=":
          case "doesNotEqual":
            return `${field} ne ${value}`;
          case ">":
            return `${field} gt ${value}`;
          case "<":
            return `${field} lt ${value}`;
          case ">=":
            return `${field} ge ${value}`;
          case "<=":
            return `${field} le ${value}`;
          case "contains":
            return `contains(${field}, ${value})`;
          case "doesNotContain":
            return `not contains(${field}, ${value})`;
          case "startsWith":
            return `startswith(${field}, ${value})`;
          case "endsWith":
            return `endswith(${field}, ${value})`;
          case "isEmpty":
            return `${field} eq null`;
          case "isNotEmpty":
            return `${field} ne null`;
          case "isAnyOf":
            const values = Array.isArray(value) ? value : [value];
            return values
              .map((v: string) => `${field} eq ${`'${v.replace(/'/g, "''")}'`}`)
              .join(" or ");
          default:
            return "";
        }
      })
      .filter(Boolean)
      .join(" and ");
  };

  const fetchData = async ({
    paginationModel,
    filterModel,
    sortModel,
  }: {
    paginationModel: GridPaginationModel;
    sortModel: GridSortModel;
    filterModel: GridFilterModel;
  }) => {
    const skip = paginationModel.page * paginationModel.pageSize;
    const sort = sortModel[0];
    const orderBy = sort ? `$orderby=${sort.field} ${sort.sort}` : "";
    const filter = buildODataFilter(filterModel.items);
    const filterQuery = filter ? `$filter=${filter}` : "";
    const countQuery = `$count=true`;

    const queryParts = [
      `$top=${paginationModel.pageSize}`,
      `$skip=${skip}`,
      orderBy,
      filterQuery,
      countQuery,
    ]
      .filter(Boolean)
      .join("&");

    const finalUrl = `${fullUrl}?${queryParts}`;

    // if (finalUrl === lastOdataUrl) {
    //   return;
    // }

    try {
      const response = await axiosInstance.get(finalUrl);
      const rawItems = Array.isArray(response.data?.value)
        ? response.data.value
        : Array.isArray(response.data)
          ? response.data
          : [];
      const values = lowercaseFirstLetterKeys(rawItems) as TModel[];

      const dateCols = internalColums.filter(
        (c) => c.type === "date" || c.type === "dateTime"
      );
      dateCols.forEach((c) =>
        (values as any[]).forEach((r) => {
          r[c.field] = new Date(r[c.field]);
        })
      );
      const items = getRowId
        ? ((values as any[]).map((item) => ({
            ...item,
            id: getRowId(item),
          })) as TModel[])
        : values;
      return {
        items,
        itemCount: response.data?.["@odata.count"] ?? values.length,
      };
    } catch (err) {
      console.error("Errore fetch OData:", err);
    } finally {
    }
    return {
      items: [],
      itemCount: 0,
    };
  };

  const prepareDataForServer = (data: any) => {
    if (trasformDataForServer) {
      data = trasformDataForServer(data);
    }
    // Handle empty strings as null for guid
    const guidField = columns.filter((c) => c.extraType === "guid");
    for (let f of guidField) {
      if (data[f.field] === "") {
        (data as any)[f.field] = null;
      }
    }
    return data;
  };

  const dataSource: DataSource<TModel> = {
    fields: internalColums,
    getMany: fetchData,
    getOne: async (entityId) => {
      const response = await axiosInstance.get(`${fullUrl}/${entityId}`);

      const fakeField = columns.filter((c) => c.getValueToShow);
      for (let f of fakeField) {
        response.data[f.field] = await f.getValueToShow!(response.data);
      }

      return response.data as TModel;
    },

    createOne: async (data) => {
      if (customCreateOne) {
        return customCreateOne(data);
      }
      try {
        const createUrl = customCreateUrl
          ? `${getBaseUrl()}/${customCreateUrl}`
          : fullUrl;
        const response = await axiosInstance.post(
          createUrl,
          prepareDataForServer(data)
        );
        return response.data as TModel;
      } catch (e: any) {
        throw new Error();
      }
    },

    updateOne: async (entityId, data) => {
      try {
        const response = await axiosInstance.put(
          `${fullUrl}/${entityId}`,
          prepareDataForServer(data)
        );
        return response.data as TModel;
      } catch (e: any) {
        throw new Error();
      }
    },
    deleteOne:
      disable?.delete ||
      !identityService.checkEndpointsAllowed(`${apiUrl}/{...}`, "DELETE")
        ? undefined
        : async (entityId) => {
            try {
              await axiosInstance.delete(`${fullUrl}/${entityId}`);
            } catch (e: any) {
              throw new Error();
            }
          },
    validate: (formValues) => {
      const issues: { message: string; path: [keyof TModel] }[] = [];
      const formColumns = columns.filter(
        (c: MagicCrudFields<TModel>) =>
          (isCreateMode && !c.notInCreateMode) ||
          (isEditMode && !c.notInEditMode)
      );
      for (const c of formColumns) {
        if (!(c as MagicCrudFields<TModel>).isRequired) continue;
        const fieldKey = c.field as keyof TModel;
        const value = formValues[fieldKey];
        const isEmpty =
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "object" &&
            value !== null &&
            !(value instanceof File) &&
            Object.keys(value).length === 0);

        if (isEmpty) {
          const fieldLabel = intl.formatMessage({
            id: c.headerName ?? c.field,
          });
          issues.push({
            message: intl.formatMessage(
              { id: "validationFieldRequired" },
              { field: fieldLabel }
            ),
            path: [fieldKey],
          });
        }
      }
      return { issues };
    },
  };

  const dataSourceCache = new DataSourceCache();

  const handleRowClick = React.useCallback(
    (id: string | number) => navigate(`${appUrl}/${id}`),
    [appUrl, navigate]
  );

  const handleCreateClick = React.useCallback(() => {
    navigate(createPath);
  }, [createPath, navigate]);

  const handleEditClick = React.useCallback(
    (id: string | number) => navigate(`${appUrl}/${id}/edit`),
    [appUrl, navigate]
  );

  const goBackToList = React.useCallback(() => {
    navigate(listPath);
  }, [listPath, navigate]);

  return (
    <Box
      sx={{
        pt: 0,
        pb: 0,
        px: 2,
        maxHeight: "100%",
        minHeight: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CrudProvider<TModel>
        dataSource={dataSource}
        dataSourceCache={dataSourceCache}
      >
        {pathname === listPath && (
          <List<TModel>
            initialPageSize={pageSize}
            onRowClick={
              disable?.show ||
              !identityService.checkEndpointsAllowed(`${apiUrl}/{...}`, "GET")
                ? undefined
                : handleRowClick
            }
            onCreateClick={
              disable?.create ||
              !identityService.checkEndpointsAllowed(
                customCreateUrl ?? apiUrl,
                "POST"
              )
                ? undefined
                : handleCreateClick
            }
            onEditClick={
              disable?.edit ||
              !identityService.checkEndpointsAllowed(`${apiUrl}/{...}`, "PUT")
                ? undefined
                : handleEditClick
            }
            localeText={crudLocaleText}
            slotProps={{
              dataGrid: {
                autoHeight: false,
                pageSize,
                initialState: { pageSize },
                pageSizeOptions: [20, 40, 80],
                ...(getRowId ? { getRowId } : {}),
              },
              pageContainer: {
                className: "crud-container",
              },
            }}
          />
        )}

        {pathname === createPath && (
          <Create<TModel>
            onSubmitSuccess={goBackToList}
            resetOnSubmit={false}
            pageTitle={
              customCreateLabel
                ? intl.formatMessage({ id: customCreateLabel })
                : `${intl.formatMessage({ id: "create" })} ${intlName}`
            }
            localeText={crudLocaleText}
            slots={{
              form: {
                select: Select,
                datePicker: (props) => <DatePicker {...props} />,
                dateTimePicker: (props) => <DateTimePicker {...props} />,
              },
            }}
          />
        )}

        {pathname !== createPath && idFromShow && !idFromEdit && (
          <Show<TModel>
            id={idFromShow}
            onEditClick={
              disable?.edit ||
              !identityService.checkEndpointsAllowed(`${apiUrl}/{...}`, "PUT")
                ? undefined
                : handleEditClick
            }
            onDelete={
              disable?.delete ||
              !identityService.checkEndpointsAllowed(
                `${apiUrl}/{...}`,
                "DELETE"
              )
                ? undefined
                : goBackToList
            }
            pageTitle={`${intl.formatMessage({ id: "details" })} ${intlName}`}
            localeText={crudLocaleText}
          />
        )}

        {idFromEdit && (
          <Edit<TModel>
            id={idFromEdit}
            onSubmitSuccess={goBackToList}
            pageTitle={`${intl.formatMessage({ id: "edit" })} ${intlName}`}
            localeText={editLocaleText}
            slots={{
              form: {
                select: Select,
                datePicker: (props) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={locale}
                  >
                    <DatePicker {...props} />
                  </LocalizationProvider>
                ),
                dateTimePicker: (props) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={locale}
                  >
                    <DateTimePicker {...props} />
                  </LocalizationProvider>
                ),
              },
            }}
          />
        )}
      </CrudProvider>
    </Box>
  );
}
